import { AxiosRequestConfig } from 'axios';
import { Locale, Translations } from '../translations/translations';
import { convertImageToBase64 } from '../utils/convert-image-to-base64';
import { isProductionEnvironment } from '../utils/utils';
import { extendApiTranslationsWithLocalTranslations, pickData, validateData } from './api-utils';
import { backendClient, publicBackendClient } from './clients';
import {
    AcceleratorAccessData,
    AccessPointData,
    AutocompletePeopleEvents,
    BannerData,
    EditEvacuationPut,
    EditFavouritesRequest,
    EvacuationData,
    EvacuationPeopleList,
    FavouritesData,
    FavouritesOperationRequest,
    FavouritesOperationResult,
    GetOne,
    GetPreferredLanguage,
    HomeData,
    ListAccessPointsData,
    ListEvacuationsData,
    NotificationData,
    PersonalData,
    PostOne,
    PutOne,
    RequestCheckAccess,
    RequestPermissionPost,
    RequestVisitorCard,
    ScannedPersonResult,
    Settings,
    TemporaryBadgeData,
    VisitorCardData,
} from './types';

const get = ({ url, config }: { url: string; config: AxiosRequestConfig<unknown> }) =>
    backendClient.get(url, config).then(pickData);
const post = ({ url, data, config }: { url: string; data: unknown; config: AxiosRequestConfig<any> }) =>
    backendClient.post(url, data, config).then(pickData);
const publicPost = ({
    url,
    data,
    config,
}: {
    url: string;
    data: Record<string, unknown>;
    config: AxiosRequestConfig<any>;
}) => publicBackendClient.post(url, data, config).then(pickData);
const put = ({ url, data, config }: { url: string; data: unknown; config: AxiosRequestConfig<any> }) =>
    backendClient.put(url, data, config).then(pickData);

const getAndValidate = ({ url, config }: { url: string; config: AxiosRequestConfig<unknown> }) =>
    get({ url, config }).then(validateData);

const postAndValidate = ({ url, data, config }: { url: string; data: unknown; config: AxiosRequestConfig<any> }) =>
    post({ url, data, config }).then(validateData);

const putAndValidate = ({ url, data, config }: { url: string; data: unknown; config: AxiosRequestConfig<any> }) =>
    put({ url, data, config }).then(validateData);

export const checkAccess: GetOne<RequestCheckAccess, AccessPointData> = (
    { language, accessPointCode, personID },
    config = {}
) =>
    getAndValidate({
        url:
            !isProductionEnvironment() && personID
                ? `/checkaccess/${language}/${accessPointCode}/${personID}`
                : `/checkaccess/${language}/${accessPointCode}`,
        config,
    });

export const fetchNFCHomepage: GetOne<{ language: Locale; rfid: string }, HomeData> = (
    { rfid, language },
    config = {}
) => getAndValidate({ url: `/rfid_homepage/${language}/${rfid}`, config });

export const checkNFCAccess: GetOne<{ language: Locale; accessPointCode: string; rfid: string }, AccessPointData> = (
    { rfid, accessPointCode, language },
    config = {}
) =>
    getAndValidate({
        url: `/rfid_checkaccess/${language}/${accessPointCode}/${rfid}`,
        config,
    });

export const fetchHomepageData: GetOne<{ language: Locale }, HomeData> = ({ language, config = {} }) =>
    getAndValidate({ url: `/homepage/${language}`, config });

export const fetchUserPicture: GetOne<{ encryptedPersonID: string }, string> = ({ encryptedPersonID, config = {} }) =>
    convertImageToBase64(
        get({
            url: `/getphoto/${encryptedPersonID}`,
            config: {
                ...config,
                responseType: 'blob',
            },
        })
    );

export const fetchTranslations: GetOne<{}, Translations> = ({ config = {} } = {}) =>
    getAndValidate({ url: 'getboilertext', config }).then(extendApiTranslationsWithLocalTranslations);

export const fetchQRCodesData: GetOne<{}, Settings> = ({ config = {} } = {}) =>
    getAndValidate({ url: 'settings', config });

export const fetchLanguage: GetOne<{}, GetPreferredLanguage> = ({ config = {} } = {}) =>
    getAndValidate({ url: 'getpreferredlanguage', config });

export const postRequestAccess: PostOne<RequestPermissionPost> = ({ data, language, config = {} }) =>
    postAndValidate({ url: `/createrequest/${language}`, data, config });

export const fetchAccessPointsList: GetOne<{ language: Locale; hint: string; page: number }, ListAccessPointsData> = ({
    language,
    hint,
    page,
    config = {},
}) => getAndValidate({ url: `/listaccesspoint/${language}`, config: { ...config, params: { hint, page } } });

export const checkRFIDOrPersonIdForEvent: GetOne<
    { language: Locale; rfidOrPersonId: string; eventId: string },
    ScannedPersonResult
> = ({ rfidOrPersonId, language, eventId }, config = {}) =>
    postAndValidate({ url: `/rfid_event/${language}/${eventId}/${rfidOrPersonId}`, data: {}, config });

export const fetchEvacuation: GetOne<{ language: Locale; eventId: string }, EvacuationData> = ({
    language,
    eventId,
    config = {},
}) => getAndValidate({ url: `/fetchevent/${language}/${eventId}`, config });

export const fetchEvacuations: GetOne<{ language: Locale; page: number }, ListEvacuationsData> = ({
    language,
    page,
    config = {},
}) => getAndValidate({ url: `/listevents/${language}`, config: { ...config, params: { hint: '', page } } });

export const fetchPeopleForEvacuation: GetOne<
    { language: Locale; eventId: string; page: number },
    EvacuationPeopleList
> = ({ language, eventId, page, config = {} }) =>
    getAndValidate({ url: `/listcheckedin/${language}/${eventId}`, config: { ...config, params: { page } } });

export const fetchPeopleForManualInput: GetOne<
    { language: Locale; query: string; page: number },
    AutocompletePeopleEvents
> = ({ language, query, page, config = {} }) =>
    getAndValidate({ url: `/listpersons/${language}/${query}`, config: { ...config, params: { page } } });

export const createEvacuation: PostOne<{ language: Locale }> = ({ language, config = {} }) =>
    postAndValidate({ url: `/createevent/${language}`, data: {}, config });

export const editEvacuation: PutOne<EditEvacuationPut> = ({ language, eventId, data, config = {} }) =>
    putAndValidate({ url: `/editevent/${language}/${eventId}`, data, config });

export const fetchMyData: GetOne<{ language: Locale }, PersonalData> = ({ language, config = {} }) =>
    getAndValidate({ url: `/mydata/${language}`, config });

export const fetchBanner: GetOne<{ language: Locale }, BannerData> = ({ language, config = {} }) =>
    getAndValidate({ url: `/getbanner/${language}`, config });

export const fetchTemporaryBadge: GetOne<{ language: Locale }, TemporaryBadgeData> = ({ language, config = {} }) =>
    postAndValidate({
        url: `/createtemporarybadge/${language}`,
        data: {},
        config,
    });

export const fetchTemporaryBadgeStatus = ({ data, language }: any, { config = {} } = {}) =>
    postAndValidate({ url: `/tempbadge_homepage/${language}`, data, config });

export const fetchVisitorCardStatus = ({ data, language }: any, { config = {} } = {}) =>
    postAndValidate({ url: `/extvisitor_homepage/${language}`, data, config });

export const fetchExternalVisitorCard: PostOne<RequestVisitorCard, VisitorCardData> = ({ token, config = {} }) =>
    publicPost({ url: `/extvisitor_create_badge/`, data: { token }, config }).then((responseData) => {
        if (responseData.error) {
            throw Object.assign(new Error(responseData.error), { link: responseData.link });
        }
        return responseData;
    });

export const fetchNotifications: GetOne<{ language: Locale }, NotificationData> = ({ language, config = {} }) =>
    getAndValidate({ url: `/notifications/${language}`, config });

export const fetchFavourites: GetOne<{ language: Locale }, FavouritesData> = ({ language, config = {} }) =>
    getAndValidate({
        url: `/favourites/${language}`,
        config,
    });

export const addFavourite: PostOne<FavouritesOperationRequest, FavouritesOperationResult> = ({
    language,
    accessPointCode,
    config = {},
}) =>
    postAndValidate({
        url: `/favourites/${language}/add/${accessPointCode}`,
        data: {},
        config,
    });

export const removeFavourite: PostOne<FavouritesOperationRequest, FavouritesOperationResult> = ({
    language,
    accessPointCode,
    config = {},
}) =>
    postAndValidate({
        url: `/favourites/${language}/remove/${accessPointCode}`,
        data: {},
        config,
    });

export const editFavourites: PostOne<EditFavouritesRequest, FavouritesOperationResult> = ({
    language,
    data,
    config = {},
}) =>
    postAndValidate({
        url: `/favourites/${language}/editlist`,
        data,
        config,
    });

export const fetchAcceleratorsAccess: GetOne<{ language: Locale }, AcceleratorAccessData> = ({
    language,
    config = {},
}) => getAndValidate({ url: `/acceleratorsaccess/${language}`, config: { ...config } });
